import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Pencil, Plus, Power, Users as UsersIcon } from 'lucide-react'

import { ApiError } from '@/api/client'
import { locationsApi, usersApi } from '@/api/endpoints'
import type { User, UserRole } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { canDeactivateUser, canEditUser, isOwner, ROLE_LABELS } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Badge, RoleBadge, StatusBadge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Card } from '@/components/ui/Card'
import { LocationBadges } from '@/components/ui/LocationBadges'
import { ConfirmDialog } from '@/components/ui/Modal'
import { Pagination } from '@/components/ui/Pagination'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ToggleChip, Toolbar } from '@/components/ui/Toolbar'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDateTime } from '@/lib/format'
import { ResetPasswordModal } from './ResetPasswordModal'
import { UserFormModal } from './UserFormModal'

const PAGE_SIZE = 20

export function UsersPage() {
  const queryClient = useQueryClient()
  const { user: actor } = useAuth()
  const { notifySuccess, notifyError } = useToast()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [role, setRole] = useState<UserRole | ''>('')
  const [locationId, setLocationId] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)

  const [editing, setEditing] = useState<User | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [resetting, setResetting] = useState<User | null>(null)
  const [toDeactivate, setToDeactivate] = useState<User | null>(null)

  const debouncedSearch = useDebouncedValue(search)

  const query = useQuery({
    queryKey: ['users', { page, search: debouncedSearch, role, locationId, includeInactive }],
    queryFn: () =>
      usersApi.list({
        page,
        size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        role: role || undefined,
        location_id: locationId || undefined,
        include_inactive: includeInactive,
      }),
    placeholderData: keepPreviousData,
  })

  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
  })

  const deactivation = useMutation({
    mutationFn: (target: User) => usersApi.deactivate(target.id),
    onSuccess: async (_, target) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      notifySuccess(`Konto ${target.full_name} zostało dezaktywowane.`)
      setToDeactivate(null)
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się dezaktywować konta.')
      setToDeactivate(null)
    },
  })

  const resetFilters = () => {
    setSearch('')
    setRole('')
    setLocationId('')
    setIncludeInactive(false)
    setPage(1)
  }

  const hasFilters = search !== '' || role !== '' || locationId !== '' || includeInactive

  const items = query.data?.items ?? []
  const isEmpty = !query.isPending && items.length === 0

  return (
    <>
      <PageHeader
        title="Konta"
        description={
          isOwner(actor)
            ? 'Zarządzasz kontami we wszystkich oddziałach.'
            : 'Widzisz konta powiązane z Twoimi oddziałami. Zakładać i edytować możesz konta nauczycieli.'
        }
        breadcrumb={['System', 'Konta']}
        action={
          <Button
            icon={<Plus size={18} />}
            onClick={() => {
              setEditing(null)
              setIsFormOpen(true)
            }}
          >
            Nowe konto
          </Button>
        }
      />

      <Toolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder="Szukaj po imieniu, nazwisku lub e-mailu…"
      >
        <select
          value={role}
          onChange={(event) => {
            setRole(event.target.value as UserRole | '')
            setPage(1)
          }}
          aria-label="Filtr roli"
          className="cursor-pointer rounded-full border-2 border-transparent bg-surface-container-low px-4 py-2.5 text-sm font-semibold text-on-surface-variant transition-colors focus:border-primary focus:bg-surface focus:outline-none"
        >
          <option value="">Wszystkie role</option>
          {(['OWNER', 'ADMIN', 'TEACHER'] as UserRole[]).map((item) => (
            <option key={item} value={item}>
              {ROLE_LABELS[item]}
            </option>
          ))}
        </select>

        <select
          value={locationId}
          onChange={(event) => {
            setLocationId(event.target.value)
            setPage(1)
          }}
          aria-label="Filtr oddziału"
          className="cursor-pointer rounded-full border-2 border-transparent bg-surface-container-low px-4 py-2.5 text-sm font-semibold text-on-surface-variant transition-colors focus:border-primary focus:bg-surface focus:outline-none"
        >
          <option value="">Wszystkie oddziały</option>
          {(locationsQuery.data?.items ?? []).map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>

        <ToggleChip
          checked={includeInactive}
          onChange={(checked) => {
            setIncludeInactive(checked)
            setPage(1)
          }}
          label="Pokaż nieaktywne"
        />

        {hasFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-sm font-bold text-primary transition-opacity hover:opacity-70"
          >
            Wyczyść filtry
          </button>
        )}
      </Toolbar>

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać listy kont. Odśwież stronę i spróbuj ponownie.
        </Alert>
      )}

      <Card>
        {query.isPending ? (
          <SkeletonRows />
        ) : isEmpty ? (
          <EmptyState
            icon={<UsersIcon size={28} />}
            title={hasFilters ? 'Brak wyników' : 'Nie ma jeszcze żadnego konta'}
            description={
              hasFilters
                ? 'Zmień kryteria wyszukiwania albo wyczyść filtry.'
                : 'Załóż konto nauczyciela, aby mógł wprowadzać swój plan zajęć i obecności.'
            }
            action={
              hasFilters ? (
                <Button variant="outline" onClick={resetFilters}>
                  Wyczyść filtry
                </Button>
              ) : (
                <Button icon={<Plus size={18} />} onClick={() => setIsFormOpen(true)}>
                  Nowe konto
                </Button>
              )
            }
          />
        ) : (
          <>
            <DataTable caption="Lista kont użytkowników">
              <TableHead
                columns={[
                  'Osoba',
                  'Rola',
                  'Oddziały',
                  'Ostatnie logowanie',
                  'Status',
                  { label: 'Akcje', srOnly: true },
                ]}
              />
              <TableBody>
                {items.map((person) => {
                  const isSelf = person.id === actor?.id
                  return (
                    <TableRow key={person.id} muted={!person.is_active}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar name={person.full_name} />
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 font-bold text-on-surface">
                              {person.full_name}
                              {isSelf && <Badge tone="info">To Ty</Badge>}
                            </p>
                            <p className="truncate text-xs text-on-surface-variant">
                              {person.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <RoleBadge role={person.role} />
                      </TableCell>
                      <TableCell>
                        <LocationBadges
                          locations={person.locations}
                          hasAll={person.has_all_locations}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-on-surface-variant">
                          {formatDateTime(person.last_login_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <StatusBadge isActive={person.is_active} />
                          {person.must_change_password && (
                            <Badge tone="warning">Zmiana hasła</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex justify-end gap-1">
                          <IconButton
                            label={`Edytuj konto ${person.full_name}`}
                            icon={<Pencil size={16} />}
                            onClick={() => {
                              setEditing(person)
                              setIsFormOpen(true)
                            }}
                            disabled={!canEditUser(actor, person)}
                          />
                          <IconButton
                            label={`Zresetuj hasło konta ${person.full_name}`}
                            icon={<KeyRound size={16} />}
                            onClick={() => setResetting(person)}
                            disabled={!canEditUser(actor, person)}
                          />
                          <IconButton
                            label={`Dezaktywuj konto ${person.full_name}`}
                            icon={<Power size={16} />}
                            onClick={() => setToDeactivate(person)}
                            disabled={!person.is_active || !canDeactivateUser(actor, person)}
                            className="hover:bg-error-container hover:text-error"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {items.length === 0 && <EmptyRow colSpan={6} message="Brak kont." />}
              </TableBody>
            </DataTable>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={query.data?.total ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <UserFormModal open={isFormOpen} user={editing} onClose={() => setIsFormOpen(false)} />

      <ResetPasswordModal
        open={resetting !== null}
        user={resetting}
        onClose={() => setResetting(null)}
      />

      <ConfirmDialog
        open={toDeactivate !== null}
        title="Dezaktywować konto?"
        message={
          toDeactivate
            ? `${toDeactivate.full_name} straci dostęp do systemu natychmiast — wszystkie aktywne sesje zostaną unieważnione. Dane historyczne, w tym przeprowadzone lekcje i rozliczenia, pozostaną nienaruszone.`
            : ''
        }
        confirmLabel="Dezaktywuj konto"
        isLoading={deactivation.isPending}
        onConfirm={() => toDeactivate && deactivation.mutate(toDeactivate)}
        onClose={() => setToDeactivate(null)}
      />
    </>
  )
}
